import { z } from "zod";

export const PERSON_STATUSES = ["active", "inactive"] as const;

/**
 * Person fields only an admin tier may see, on ANY profile including the
 * viewer's own.
 *
 * Note this is a stricter rule than `phone`, which is admin-visible on other
 * people's records but always visible on your own — hence a separate list
 * rather than a shared "sensitive fields" set. Variable Compensation is
 * compensation-adjacent: an employee seeing their own VC start date is the
 * disclosure, not an exception to it.
 *
 * Enforced server-side by `redactAdminOnlyPersonFields` (the value must not
 * ship in the payload at all — hiding the row in the UI would still leak it),
 * and used client-side to drop the field from the profile, its scheduled-change
 * icon, and its change history.
 */
export const ADMIN_ONLY_PERSON_FIELDS = ["vcStartDate", "notes"] as const;

/**
 * Strips the `canceled:` prefix before matching, so a withdrawn scheduled change
 * is redacted exactly like the field it withdraws.
 *
 * `cancelPendingChange` records the withdrawal as a synthetic diff key —
 * `canceled:vcStartDate` — carrying the value that was queued. Matching the raw
 * key let that row through the admin-only filter, so an admin scheduling a VC
 * start date change and then cancelling it published the date to the person's
 * own History tab. `resolverFor` in audit-resolve.ts already strips the prefix
 * the same way; doing it here fixes every caller at once rather than one.
 */
export function isAdminOnlyPersonField(field: string): boolean {
  const bare = field.startsWith("canceled:") ? field.slice("canceled:".length) : field;
  return (ADMIN_ONLY_PERSON_FIELDS as readonly string[]).includes(bare);
}

// NOTE: employment type, role classification and work location have NO
// constants here. All three were promoted to admin-managed tables
// (`employment_type` in EPIC-028, `role_classification` and `work_location`
// after it), so their valid sets and display labels are data, not code —
// clients read them from `useEmploymentTypes()` / `useRoleClassifications()` /
// `useWorkLocations()`, the server from the matching `@/api/*` module, and the
// write paths validate existence at runtime (`employmentTypeError`,
// `roleClassificationError`, `workLocationError`) rather than with `z.enum`.
// Keeping a shadow copy of any of those unions here is how a list drifts from
// the config it is supposed to describe.
//
// `status` stays a real union on purpose: it is not reference data. Code
// branches on active/inactive throughout (directory visibility, headcount,
// PTO eligibility), so an admin adding a third value would break behaviour
// rather than configure it.

export const PERSON_STATUS_LABELS: Record<(typeof PERSON_STATUSES)[number], string> = {
  active: "Active",
  inactive: "Inactive",
};

// 24-hour clock time, "HH:MM" or "HH:MM:SS" (Postgres `time` round-trips with seconds).
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;
// Accept "" as well so an empty <input type="time"> doesn't block form validation;
// the form/API normalize "" to NULL before persisting.
const optionalTimeField = z
  .union([z.string().regex(TIME_PATTERN, "Use a 24-hour time, e.g. 09:00"), z.literal("")])
  .nullable()
  .optional();

// Date-only "YYYY-MM-DD" (what <input type="date"> produces). Empty string is
// accepted and normalized to NULL so a cleared date input doesn't fail.
const optionalDateOnly = z
  .union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date, e.g. 2026-01-15"), z.literal("")])
  .nullable()
  .optional();

// Field set WITHOUT defaults — the base for both variants. Defaults live only
// on the create schema: zod's .partial() still applies .default() values for
// absent keys, so a defaulted update schema would silently inject
// employmentType/status into every partial edit (and the effective-date flow
// would schedule changes the user never made).
const personFieldsSchema = z.object({
  name: z.string().min(1, "Name is required").max(500, "Name is too long"),
  title: z.string().max(500).optional(),
  // The person's work email — required (WI-015 retained contact model). Rows
  // predating the requirement may be empty until their next edit.
  email: z.string().min(1, "Work email is required").max(500).email("Enter a valid email address"),
  // Optional; the form submits "" when cleared (normalized to NULL at persist),
  // so empty must pass while a non-empty value must be a real address.
  personalEmail: z
    .string()
    .max(500)
    .email("Enter a valid email address")
    .or(z.literal(""))
    .optional(),
  phone: z.string().max(100).optional(),
  // B2B billing entity for contractors; only shown when employmentType is
  // "contractor" but accepted regardless so the value survives type changes.
  entityName: z.string().max(500).optional(),
  // Validated at runtime against the active `employment_type` rows (see
  // `employmentTypeError`), not with `z.enum` — the list is admin-managed.
  employmentType: z.string().min(1, "Employment type is required"),
  status: z.enum(PERSON_STATUSES),
  // Both are `key` tokens validated at runtime against their admin-managed
  // tables (`workLocationError` / `roleClassificationError`), for the same
  // reason as `employmentType` above. Null is a real value on both — "not
  // recorded" — so neither has a `.min(1)`.
  location: z.string().nullable().optional(),
  roleClassification: z.string().nullable().optional(),
  hoursPerWeek: z.number().int().min(0).max(80, "Max 80 hours").nullable().optional(),
  workingHoursStart: optionalTimeField,
  workingHoursEnd: optionalTimeField,
  workingHoursStart2: optionalTimeField,
  workingHoursEnd2: optionalTimeField,
  workingHoursNote: z.string().max(2000).optional(),
  startDate: optionalDateOnly,
  rehireDate: optionalDateOnly,
  // Variable Compensation start date. Admin-only (absent from
  // personSelfContactSchema); mirrors NOS Person.StartDateVC.
  vcStartDate: optionalDateOnly,
  // Employee profile type (Builder / Connector / Craftsperson) — reference
  // data, admin-only. Mirrors NOS Person.EmployeeProfileID.
  employeeProfileId: z.string().uuid().nullable().optional(),
  // Employment end date. When reached, the background worker auto-flips status
  // to "inactive".
  endDate: optionalDateOnly,
  notes: z.string().max(5000).optional(),
  userId: z.string().nullable().optional(),
  departmentId: z.string().uuid().nullable().optional(),
  // Role team within the department. The API validates it belongs to
  // `departmentId`; the form filters the picker to that department's teams.
  roleTeamId: z.string().uuid().nullable().optional(),
  managerId: z.string().uuid().nullable().optional(),
  // Mentor relationship, distinct from the manager. Zero or one; self-reference
  // is rejected server-side (mentorship isn't a reporting line, so no cycle walk).
  dottedLineId: z.string().uuid().nullable().optional(),
});

export const personCreateSchema = personFieldsSchema.extend({
  employmentType: z.string().min(1, "Employment type is required").default("full_time"),
  status: z.enum(PERSON_STATUSES).default("active"),
});

export const personUpdateSchema = personFieldsSchema.partial();

/**
 * Self-service contact edit — the ONLY fields a non-admin may change on their
 * own record, applied immediately (no effective date). Photo is handled
 * separately via the avatar route. Everything else stays admin-only.
 */
export const personSelfContactSchema = personFieldsSchema
  .pick({
    personalEmail: true,
    phone: true,
    location: true,
    workingHoursStart: true,
    workingHoursEnd: true,
    workingHoursStart2: true,
    workingHoursEnd2: true,
    workingHoursNote: true,
  })
  .partial();

export type PersonCreateInput = z.infer<typeof personCreateSchema>;
export type PersonUpdateInput = z.infer<typeof personUpdateSchema>;
export type PersonSelfContactInput = z.infer<typeof personSelfContactSchema>;

/**
 * A person's free-text request to have their record updated by someone else
 * (e.g. "please change my manager to X"). Emailed to the configured
 * update-request recipients; not applied automatically.
 */
export const selfUpdateRequestSchema = z.object({
  message: z.string().min(1, "Describe the change you need").max(2000, "Message is too long"),
});

/**
 * Request envelope for a person edit. `effectiveDate` is optional: omitted, the
 * change applies immediately (the UI's "Select a date for this change" box is
 * unchecked — the default). When present, any date is allowed — today/past
 * applies immediately, a future date is stored as a pending change the worker
 * applies on the date.
 */
export const personUpdateRequestSchema = z.object({
  effectiveDate: z.coerce.date({ message: "Enter a valid effective date" }).optional(),
  updates: personUpdateSchema,
  // "Overwrite the scheduled change" choice: still-pending scheduled changes to
  // withdraw in the same transaction as this save (ids from the pending list).
  cancelPendingChangeIds: z.array(z.string().uuid()).optional(),
  // Why the change was made (a change_reason id). The save dialog collects it
  // when an org-placement field changed; stamped on the audit row.
  changeReasonId: z.string().uuid().nullable().optional(),
});

/**
 * Deactivate a person and reassign their direct reports atomically (WI-076).
 * `updates` is the person's own edit (status → inactive, plus any other fields
 * changed in the same save); `reassignments` moves every direct report to a
 * new manager (`null` = no manager) on the same date. `effectiveDate` omitted
 * ⇒ everything applies immediately.
 */
export const personDeactivateSchema = z.object({
  effectiveDate: z.coerce.date({ message: "Enter a valid effective date" }).optional(),
  updates: personUpdateSchema,
  cancelPendingChangeIds: z.array(z.string().uuid()).optional(),
  changeReasonId: z.string().uuid().nullable().optional(),
  reassignments: z.array(
    z.object({
      personId: z.string().uuid(),
      managerId: z.string().uuid().nullable(),
    }),
  ),
});

export const personRoleAssignmentSchema = z
  .object({
    roleId: z.string().uuid(),
    isPrimary: z.boolean().default(false),
    /**
     * Which of the role's career levels the person holds (EPIC-033). Nullable
     * throughout: a role need not opt into any level, and assignments predating
     * levels have none. The server checks a supplied level is one the role
     * actually applies — see `assertRoleLevelsApplicable`.
     */
    roleLevelId: z.string().uuid().nullable().optional(),
    startDate: optionalDateOnly,
    endDate: optionalDateOnly,
  })
  .refine((a) => !a.startDate || !a.endDate || a.endDate >= a.startDate, {
    message: "End date can't be before the start date",
    path: ["endDate"],
  });

export const setPersonRolesSchema = z.object({
  // Role changes are effective-dated like every other person field (WI-055):
  // today/past applies now, a future date schedules a pending change.
  effectiveDate: z.coerce.date({ message: "An effective date is required" }),
  assignments: z.array(personRoleAssignmentSchema),
});

export type PersonRoleAssignment = z.infer<typeof personRoleAssignmentSchema>;

// Link an already-uploaded file (from the /api/files presign→confirm flow) to a
// person as their profile picture. The file's content-type is validated
// server-side; here we only check the id shape.
export const setPersonAvatarSchema = z.object({
  fileId: z.string().uuid(),
});

/**
 * Flag a role as aspirational for a person, optionally naming the career level
 * being worked toward ("Senior Solution Architect" rather than just "Solution
 * Architect"). The same role may be flagged at several levels — one entry per
 * role-and-level pair; the identical pair is refused.
 *
 * `roleLevelId` is nullable/optional throughout: roles need not have a ladder,
 * and "that role, level unspecified" is a legitimate aspiration. The server
 * checks a supplied level is one the role actually applies.
 */
export const addAspirationSchema = z.object({
  roleId: z.string().uuid(),
  roleLevelId: z.string().uuid().nullable().optional(),
});

/** Retarget one listed aspiration's career level (null = back to any level). */
export const setAspirationLevelSchema = z.object({
  roleLevelId: z.string().uuid().nullable(),
});

/**
 * Rewrite the priority order — the full list of ASPIRATION ids in the desired
 * order. Not role ids: a role can be listed twice at different levels.
 */
export const reorderAspirationsSchema = z.object({
  aspirationIds: z.array(z.string().uuid()).min(1),
});
